'use client';

/**
 * VendorResponseDetails - Vendor response entity detail view
 *
 * @deprecated Use EntityDetailsLoader with DetailsPanel instead.
 * This component is kept for fallback during migration.
 * Remove after schema-driven rendering is verified.
 *
 * Displays vendor quote information when a vendor_response node is selected.
 * Fetches its own data via useVendorResponseExplore hook.
 *
 * @component_role VendorResponse Detail View
 * @business_rule Shows quote status, dates, total amount, vendor info,
 *   RFQ context, and line items with can-supply indicators. Links to Odoo.
 * @user_outcome Quick quote assessment - see pricing, availability, lead times
 *   for all quoted items at a glance.
 * @integration_point klyde_edge /neo/graph/explore/vendor-response
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { cn } from '@/lib/utils/cn';
import {
  Factory,
  FileText,
  ExternalLink,
  Loader2,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { useVendorResponseExplore } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

interface VendorResponseDetailsProps {
  nodeId: string;
}

export function VendorResponseDetails({ nodeId }: VendorResponseDetailsProps) {
  const vendorResponseStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_response'));
  const { data, isLoading } = useVendorResponseExplore(vendorResponseStrategy!, { response_cdk: nodeId });
  const response = data?.response;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading response...
      </div>
    );
  }

  if (!response) {
    return (
      <div className="text-sm text-muted-foreground">
        Response details not available
      </div>
    );
  }

  const ODOO_HOST = process.env.NEXT_PUBLIC_ODOO_URL || 'http://klyde.vivita-ai.com';

  return (
    <div className="space-y-4">
      {/* Status Badge */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-1">STATUS</h4>
        <span className={cn(
          "inline-block px-2 py-0.5 rounded text-xs",
          response.state === 'submitted' && "bg-success/20 text-success",
          response.state === 'draft' && "bg-muted text-muted-foreground",
          response.state === 'revised' && "bg-info/20 text-info",
          !response.state && "bg-muted text-muted-foreground"
        )}>
          {response.state || 'unknown'}
        </span>
      </div>

      {/* Key Dates & Amount */}
      <div className="grid grid-cols-2 gap-2">
        {response.response_date && (
          <div className="p-2 rounded bg-card border border-border text-center">
            <div className="text-xs text-muted-foreground">Response Date</div>
            <div className="text-sm font-medium">{response.response_date}</div>
          </div>
        )}
        {response.validity_date && (
          <div className="p-2 rounded bg-card border border-border text-center">
            <div className="text-xs text-muted-foreground">Valid Until</div>
            <div className="text-sm font-medium">{response.validity_date}</div>
          </div>
        )}
        {response.total_amount != null && (
          <div className="p-2 rounded bg-card border border-border text-center col-span-2">
            <div className="text-lg font-semibold text-success">
              ${response.total_amount.toFixed(2)}
            </div>
            <div className="text-xs text-muted-foreground">Total Quote</div>
          </div>
        )}
      </div>

      {/* Vendor Info */}
      {response.vendor && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-1">VENDOR</h4>
          <div className="flex items-center gap-2 p-2 rounded bg-card border border-border">
            <Factory className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">{response.vendor.name}</div>
              {response.vendor.type && (
                <div className="text-xs text-muted-foreground capitalize">{response.vendor.type}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* RFQ Context */}
      {response.rfq && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-1">RFQ</h4>
          <div className="flex items-center gap-2 p-2 rounded bg-card border border-border">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">{response.rfq.name || response.rfq.semantic_id}</div>
              {response.rfq.status && (
                <span className={cn(
                  "text-xs px-1.5 py-0.5 rounded",
                  response.rfq.status === 'sent' && "bg-info/20 text-info",
                  response.rfq.status === 'done' && "bg-success/20 text-success"
                )}>
                  {response.rfq.status}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Line Items Table */}
      {response.line_items.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">
            QUOTED ITEMS ({response.line_items.length})
          </h4>
          <div className="border border-border rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-2 py-1">Product</th>
                  <th className="text-right px-2 py-1">Qty</th>
                  <th className="text-right px-2 py-1">Price</th>
                  <th className="text-right px-2 py-1">Days</th>
                </tr>
              </thead>
              <tbody>
                {response.line_items.map((item, idx) => (
                  <tr key={item.cdk || idx} className="border-t border-border">
                    <td className="px-2 py-1 truncate max-w-[100px]">
                      <div className="flex items-center gap-1">
                        {item.can_supply === true && <CheckCircle className="h-3 w-3 text-success shrink-0" />}
                        {item.can_supply === false && <XCircle className="h-3 w-3 text-destructive shrink-0" />}
                        <span>{item.product_name || item.product_sku || 'Item'}</span>
                      </div>
                    </td>
                    <td className="text-right px-2 py-1">{item.quantity_offered ?? '-'}</td>
                    <td className="text-right px-2 py-1">
                      {item.unit_price != null ? `$${item.unit_price.toFixed(0)}` : '-'}
                    </td>
                    <td className="text-right px-2 py-1">{item.lead_time_days ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Notes */}
      {response.notes && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-1">NOTES</h4>
          <p className="text-sm text-muted-foreground line-clamp-3">
            {response.notes}
          </p>
        </div>
      )}

      {/* CDK ID */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-1">CDK ID</h4>
        <code className="text-xs bg-muted px-2 py-1 rounded block overflow-x-auto">
          {response.cdk}
        </code>
      </div>

      {/* Open in Odoo */}
      {response.odoo_id && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">ACTIONS</h4>
          <a
            href={`${ODOO_HOST}/web#id=${response.odoo_id}&model=purchase.order&view_type=form`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-accent transition-colors text-sm"
          >
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{response.vendor?.name || 'Vendor Response'}</span>
          </a>
        </div>
      )}
    </div>
  );
}
